import { storeAuthSession } from './authSession.ts';

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function hasSessionHandoff(browser: Window = window): boolean {
  return new URLSearchParams(browser.location.hash.slice(1)).has('fpt_sso');
}

export function tokenHotelId(token: string): string {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return '';
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(base64), character => character.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes));
    return typeof claims.hotel_id === 'string' && uuidPattern.test(claims.hotel_id) ? claims.hotel_id : '';
  } catch { return ''; }
}

/** Exact origin, opener, nonce and server-validated hotel must all agree. */
export function beginSessionHandoff(
  onSuccess: () => void,
  onError: (message: string) => void,
  browser: Window = window,
  fetcher: typeof fetch = fetch,
  timeoutMs = 20000,
): () => void {
  const origins: Record<string, string> = {
    'https://fpt.dief360.com': 'https://hebergement.dief360.com',
    'https://fpt.dev.dief360.com': 'https://devhotel.dief360.com',
    'http://localhost:3005': 'http://localhost:3000',
  };
  const sourceOrigin = origins[browser.location.origin];
  const opener = browser.opener;
  const nonce = new URLSearchParams(browser.location.hash.slice(1)).get('fpt_sso') || '';
  const hotelId = new URLSearchParams(browser.location.search).get('establishment_id') || '';
  if (!sourceOrigin || !opener || !uuidPattern.test(nonce) || !uuidPattern.test(hotelId)) {
    onError('Lien de connexion FPT invalide. Ouvrez le portail depuis votre espace hôtel.');
    return () => {};
  }
  let finished = false;
  let validating = false;
  const controller = new AbortController();
  const cleanup = () => {
    clearTimeout(timer);
    controller.abort();
    browser.removeEventListener('message', receive);
  };
  const finish = (error?: string) => {
    if (finished) return;
    finished = true;
    cleanup();
    browser.history.replaceState(null, '', browser.location.pathname + browser.location.search);
    opener.postMessage({ type: error ? 'FPT_SSO_ERROR' : 'FPT_SSO_DONE', nonce }, sourceOrigin);
    browser.opener = null;
    if (error) onError(error); else onSuccess();
  };
  const receive = async (event: MessageEvent) => {
    if (finished || validating || event.origin !== sourceOrigin || event.source !== opener ||
        event.data?.type !== 'FPT_SSO_SESSION' || event.data.nonce !== nonce) return;
    if (event.data.hotelId !== hotelId || typeof event.data.token !== 'string' || tokenHotelId(event.data.token) !== hotelId) {
      finish('La session ne correspond pas à cet établissement.');
      return;
    }
    validating = true;
    try {
      // This authenticated request verifies the token at the server. No caller-supplied tenant headers.
      const response = await fetcher('/api/v1/hotel/settings', {
        headers: { Authorization: 'Bearer ' + event.data.token }, signal: controller.signal,
      });
      if (!response.ok) throw new Error('Invalid session');
      const body = await response.json();
      const profile = body.data || body;
      const verifiedHotelId = profile.hotelId || profile.hotel_id || profile.id;
      if (verifiedHotelId !== hotelId) throw new Error('Hotel mismatch');
      if (finished) return;
      storeAuthSession(event.data.token, { hotel_id: hotelId, name: profile.hotelName || profile.hotel_name || profile.name || '', establishment_count: 1 }, browser);
      finish();
    } catch {
      finish('La session n’a pas pu être vérifiée. Reconnectez-vous depuis votre portail hôtel.');
    }
  };
  const timer = setTimeout(() => finish('La connexion depuis le portail hôtel a expiré. Réessayez.'), timeoutMs);
  browser.addEventListener('message', receive);
  opener.postMessage({ type: 'FPT_SSO_READY', nonce }, sourceOrigin);
  // React StrictMode may restart this effect; cancellation must not consume the handoff.
  return () => { finished = true; cleanup(); };
}
